import { Collection } from "intake_shared/src/enum";
import { TRequestedService, UpdatedFrom } from "intake_shared/src/types";
import firebase from "firebase/compat/app";
import db from "../utils/firestore";

// The departments collection is read at app start by both getDepartments()
// and fetchDepartmentAndServices(). Concurrent callers share one read.
let inFlightDepartmentDocs: Promise<
    firebase.firestore.QueryDocumentSnapshot[]
> | null = null;
export function readDepartmentDocs() {
    if (!inFlightDepartmentDocs) {
        inFlightDepartmentDocs = db
            .collection(Collection.DEPARTMENTS)
            .get()
            .then((snapshot) => snapshot.docs)
            .finally(() => {
                inFlightDepartmentDocs = null;
            });
    }
    return inFlightDepartmentDocs;
}

export interface ClinicianServiceSelection {
    [clinician_id: string]: {
        services: string[];
        departments: string[];
    };
}

export async function updateClinicianServiceSelection(
    selection: ClinicianServiceSelection
) {
    const batch = db.batch();
    Object.entries(selection).forEach(
        async ([clinician_id, { services, departments }]) => {
            // Update clinician service selection
            const clinicianServiceDoc = db
                .collection(Collection.CLINICIAN_SERVICE_SELECTION)
                .doc(clinician_id);
            batch.set(
                clinicianServiceDoc,
                { services, departments },
                { merge: true }
            );

            // Update clinician data in the clinicians document
            const clinicianDoc = db
                .collection(Collection.CLINICIANS)
                .doc(clinician_id);
            await clinicianDoc.update({
                departments: departments,
                updated_from: UpdatedFrom.INTAKE,
            });
        }
    );
    await batch.commit();
}

export async function fetchAllCliniciansServiceSelection(): Promise<ClinicianServiceSelection> {
    const clinician_docs = await db
        .collection(Collection.CLINICIAN_SERVICE_SELECTION)
        .get();
    const data = clinician_docs.docs.reduce((acc, clinician_doc) => {
        const { services, departments } = clinician_doc.data();
        acc[clinician_doc.id] = { services, departments };
        return acc;
    }, {} as ClinicianServiceSelection);
    return data;
}

export async function fetchClinicianServiceSelection(
    clincian_id: string
): Promise<ClinicianServiceSelection> {
    const clinician_doc = await db
        .collection(Collection.CLINICIAN_SERVICE_SELECTION)
        .doc(clincian_id)
        .get();
    const { services = [], departments = [] } =
        (clinician_doc.data() as {
            services: string[];
            departments: string[];
        }) || {};
    return { [clincian_id]: { services, departments } };
}

// function to map services to a department
export async function mapServicesToADepartment(
    selectedDepts: TRequestedService,
    selectedServices: TRequestedService
) {
    if (
        Object.keys(selectedDepts).length === 0 ||
        Object.keys(selectedServices).length === 0
    ) {
        throw new Error("Both selected departments and services must be provided.");
    }

    const batch = db.batch();
    const departmentsRef = db.collection(Collection.DEPARTMENTS);

    for (const departmentId in selectedDepts) {
        const departmentDocRef = departmentsRef.doc(departmentId);

        // Retrieve the current document data
        const departmentDoc = await departmentDocRef.get();
        let currentServices = [];

        if (departmentDoc.exists) {
            const departmentData = departmentDoc.data();
            if (departmentData && departmentData.services) {
                currentServices = departmentData?.services;
            }
        }

        // Merge existing services with the new ones
        const newServices = Object.keys(selectedServices);
        const updatedServices = Array.from(
            new Set([...currentServices, ...newServices])
        );

        // Update the document with all the services
        batch.set(departmentDocRef, { services: updatedServices }, { merge: true });
    }

    await batch.commit();
}

// function to get all department and services
export async function fetchDepartmentAndServices() {
    const departmentDocs = await readDepartmentDocs();

    const departmentServicesMap = {};

    departmentDocs.forEach((doc) => {
        departmentServicesMap[doc.id] = doc.data();
    });

    return departmentServicesMap;
}

// function to delete a service from a  department
export async function deleteServiceFromDepartment(
    departmentId: string,
    serviceId: string
) {
    const departmentRef = db.collection(Collection.DEPARTMENTS).doc(departmentId);
    try {
        const departmentDoc = await departmentRef.get();
        if (!departmentDoc.exists) {
            throw new Error("Department not found");
        }
        const departmentData = departmentDoc.data();
        if (departmentData && departmentData.services) {
            const updatedServices = departmentData.services.filter(
                (existingServiceId) => existingServiceId !== serviceId
            );
            await departmentRef.update({ services: updatedServices });
        }
        return true;
    } catch (error) {
        throw new Error("Error deleting service from department");
    }
}
